import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryCreate, queryGet } from './fetch';
import { AxiosError } from 'axios';

enum PromoCodeError {
  INVALID_PROMO_CODE = 'INVALID_PROMO_CODE',
  PROMO_CODE_EXPIRED = 'PROMO_CODE_EXPIRED',
  PROMO_CODE_USED_UP = 'PROMO_CODE_USED_UP',
}

interface CodePromoCodeDto {
  code: string;
}

interface PromoCode {
  id: string;
  name: string;
  expirationDate?: Date;
  numberOfCredits: number;
  numberOfUses?: number;
  updatedAt: Date;
  createdAt: Date;
  referralsOrganizationId?: string;
}

enum ReferralStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
}

interface SponsoredChildDto {
  id: string;
  email: string;
  status: ReferralStatus;
  date: Date;
}

export interface PromoCodeVerification {
  isValid: boolean;
  error?: PromoCodeError;
}

export const useVerifyPromoCode = () => {
  return useMutation<PromoCodeVerification, undefined, CodePromoCodeDto>(
    (dto: CodePromoCodeDto) =>
      queryCreate(`/v2/promo-code/verify`)({ code: dto.code }),
  );
};

export const useReferralCode = (id?: string) => {
  return useQuery<PromoCode>(
    ['promo-code', 'referrals', id],
    queryGet(`/v2/promo-code/referrals/${id}`),
    {
      enabled: !!id,
    },
  );
};

export const useGenerateReferralCode = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<PromoCode, AxiosError, void>(
    queryCreate(`/v2/promo-code/referrals/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['promo-code', 'referrals', id]);
      },
    },
  );
};

export const useSponsoredChild = (id?: string) => {
  return useQuery<SponsoredChildDto[]>(
    ['promo-code', 'referrals', id, 'sponsored-children'],
    queryGet(`/v2/promo-code/referrals/${id}/sponsored-children`),
    {
      enabled: !!id,
    },
  );
};
